import { Button, Modal, pantryColors, pantryTypography } from '@pantry/ui'
import { Formik } from 'formik'
import React from 'react'
import * as Yup from 'yup'
import type { Review, ReviewStatus } from '../../../types/review'
import { REVIEW_DIALOG_STATUS_OPTIONS } from '../constants'
import { formatReviewDate, formatReviewTime } from '../utils/dateFormat'
import { stripHtml } from '../utils/stripHtml'
import { RelatedProductCard } from './RelatedProductCard'
import { StarRating } from './StarRating'

const p = pantryColors
const F = pantryTypography.fontFamily

export interface ReviewDetailFormValues {
  review: string
  rating: number
  status: ReviewStatus
}

interface ReviewDetailModalProps {
  storeId: string
  review: Review | null
  isSaving: boolean
  onClose: () => void
  onSave: (values: ReviewDetailFormValues) => void
  onTrash: () => void
}

const schema = Yup.object({
  review: Yup.string().trim().required('Please enter a Customer Comment'),
})

const labelStyle: React.CSSProperties = { fontSize: 12, fontWeight: 600, color: p.gray[500], marginBottom: 6, fontFamily: F }
const fieldWrapStyle: React.CSSProperties = { marginBottom: 16 }
const inputBaseStyle: React.CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  padding: '8px 11px',
  border: `1px solid ${p.gray[200]}`,
  borderRadius: 6,
  fontSize: 13.5,
  color: p.gray[800],
  fontFamily: F,
  outline: 'none',
}

export const ReviewDetailModal: React.FC<ReviewDetailModalProps> = ({ storeId, review, isSaving, onClose, onSave, onTrash }) => {
  if (!review) return null

  const initialValues: ReviewDetailFormValues = {
    review: stripHtml(review.review),
    rating: review.rating,
    status: review.status,
  }

  return (
    <Formik initialValues={initialValues} validationSchema={schema} enableReinitialize onSubmit={onSave}>
      {({ values, errors, touched, setFieldValue, handleChange, submitForm }) => (
        <Modal
          open
          title={`Review #${review.id}`}
          width={640}
          onClose={onClose}
          footer={
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              {review.status !== 'trash' ? (
                <Button variant="danger-ghost" disabled={isSaving} onClick={onTrash}>Move to Trash</Button>
              ) : <span />}
              <div style={{ display: 'flex', gap: 8 }}>
                <Button variant="ghost" onClick={onClose} disabled={isSaving}>Cancel</Button>
                <Button variant="primary" onClick={submitForm} disabled={isSaving}>{isSaving ? 'Saving…' : 'Save'}</Button>
              </div>
            </div>
          }
        >
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 220px', gap: 24 }}>
            <div>
              <div style={fieldWrapStyle}>
                <div style={labelStyle}>Customer</div>
                <input value={review.reviewer} disabled style={{ ...inputBaseStyle, background: p.gray[50], color: p.gray[500] }} />
              </div>
              <div style={fieldWrapStyle}>
                <div style={labelStyle}>Rating</div>
                <StarRating value={values.rating} readOnly={false} size={18} onChange={(v) => setFieldValue('rating', v)} />
              </div>
              <div style={fieldWrapStyle}>
                <div style={labelStyle}>Customer Comment</div>
                <textarea
                  name="review"
                  value={values.review}
                  onChange={handleChange}
                  rows={6}
                  style={{ ...inputBaseStyle, resize: 'vertical' }}
                />
                {touched.review && errors.review ? (
                  <div style={{ color: p.red[700], fontSize: 12, marginTop: 4 }}>{errors.review}</div>
                ) : null}
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              <div>
                <div style={labelStyle}>Status</div>
                <select
                  name="status"
                  value={values.status}
                  onChange={handleChange}
                  style={{ ...inputBaseStyle, marginBottom: 10 }}
                >
                  {REVIEW_DIALOG_STATUS_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <div style={{ fontSize: 11.5, color: p.gray[400] }}>
                  Submitted {formatReviewDate(review.dateCreatedGmt)} at {formatReviewTime(review.dateCreatedGmt)}
                </div>
              </div>

              <RelatedProductCard storeId={storeId} productId={review.productId} productName={review.productName} productSlug={review.productSlug} />
            </div>
          </div>
        </Modal>
      )}
    </Formik>
  )
}
